import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, CalendarDays, Compass } from "lucide-react";
import { scrollToId } from "@/lib/site";
import { Stamp, WaveBand, Gull, PipitoneButton } from "@/components/decor";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ i, children, className = "" }) => (
  <span className={`block overflow-hidden pb-[0.16em] -mb-[0.16em] ${className}`}>
    <motion.span
      custom={i}
      variants={lineVariants}
      initial="hidden"
      animate="show"
      className="block will-change-transform"
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yFlyer = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 pb-40 sm:pt-36" data-testid="hero-section">
      <div
        aria-hidden
        className="absolute -left-40 top-10 h-[480px] w-[480px] rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(13,148,136,0.16), transparent 65%)" }}
      />
      <div
        aria-hidden
        className="absolute -right-32 top-64 h-[420px] w-[420px] rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(12,27,51,0.12), transparent 65%)" }}
      />

      <Gull className="animate-gull absolute left-[8%] top-32 w-10 opacity-40" />
      <Gull className="animate-gull absolute left-[16%] top-44 w-7 opacity-30" style={{ animationDelay: "1.6s" }} />
      <Gull className="animate-gull absolute right-[12%] top-28 hidden w-9 opacity-35 lg:block" style={{ animationDelay: "3s" }} />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 text-xs font-bold uppercase tracking-[0.22em] text-[#0D9488]"
            data-testid="hero-eyebrow"
          >
            Psicoeducazione e auto-aiuto per adulti con ADHD
          </motion.p>

          <h1 className="font-display text-[17vw] font-semibold leading-[0.95] tracking-tight text-[#0C1B33] sm:text-8xl lg:text-[7.5rem]" data-testid="hero-title">
            <MaskedLine i={0}>Arcipelago</MaskedLine>
          </h1>

          <div className="mt-6 max-w-xl text-lg leading-relaxed text-[#334155] sm:text-xl" data-testid="hero-subtitle">
            <MaskedLine i={1}>
              <span className="font-display italic text-[#0D9488]">Un percorso di psicoeducazione</span>
            </MaskedLine>
            <MaskedLine i={2}>per adulti con ADHD — di gruppo o individuale,</MaskedLine>
            <MaskedLine i={3}>in presenza a Lugano Paradiso oppure online.</MaskedLine>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 flex items-center gap-2 text-sm text-[#475569]"
            data-testid="hero-location"
          >
            <MapPin className="h-4 w-4 text-[#0D9488]" />
            Via Generale Guisan 8B, 6900 Lugano Paradiso
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4"
          >
            <button
              data-testid="hero-cta-percorso"
              onClick={() => scrollToId("percorsi")}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0C1B33] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(12,27,51,0.3)] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#0D9488] hover:shadow-[0_14px_36px_rgba(13,148,136,0.4)] sm:w-auto"
            >
              <Compass className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
              Scegli il tuo percorso
            </button>
            <button
              data-testid="hero-cta-incontri"
              onClick={() => scrollToId("incontri")}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#0C1B33]/25 bg-[#FFFDF9]/70 px-7 py-3.5 text-sm font-semibold text-[#0C1B33] backdrop-blur transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 hover:border-[#0D9488] hover:bg-[#0D9488]/10 sm:w-auto"
            >
              <CalendarDays className="h-4 w-4 text-[#0D9488]" />
              Scopri i 6 incontri
            </button>
            <PipitoneButton testId="pipitone-link-hero" />
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yFlyer }} className="relative lg:col-span-5" data-testid="hero-flyer-wrap">
          <div className="mx-auto flex max-w-md items-start justify-center gap-4 sm:gap-6">
            {[
              { src: "/locandine/arcipelago-2027-gen-giu.jpg", label: "Gennaio – Giugno 2027", rot: -3, delay: 0.45 },
              { src: "/locandine/arcipelago-2027-lug-dic.jpg", label: "Luglio – Dicembre 2027", rot: 3, delay: 0.62 },
            ].map((f, idx) => (
              <motion.figure
                key={f.src}
                data-testid={`hero-flyer-${idx + 1}`}
                initial={{ opacity: 0, y: 50, rotate: f.rot * 2 }}
                animate={{ opacity: 1, y: 0, rotate: f.rot }}
                transition={{ duration: 1.1, delay: f.delay, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-1/2"
              >
                <div className="absolute -inset-3 rounded-[2rem] bg-[#0D9488]/10 blur-xl" aria-hidden />
                <img
                  src={f.src}
                  alt={`Locandina Arcipelago 2027 — ${f.label}`}
                  className="relative w-full rounded-xl border border-[#E2D9CD] shadow-[0_24px_50px_rgba(12,27,51,0.25)]"
                />
                <figcaption className="mt-3 text-center text-[11px] font-bold uppercase tracking-[0.16em] text-[#0D9488]">
                  {f.label}
                </figcaption>
              </motion.figure>
            ))}
          </div>
          <Stamp testId="hero-stamp" className="absolute -right-2 -top-8 h-24 w-24 bg-[#FAF8F5]/70 backdrop-blur-[2px] sm:-right-6 sm:h-28 sm:w-28" />
        </motion.div>
      </div>

      <WaveBand className="absolute inset-x-0 bottom-0 h-24 sm:h-32" tone="navy" />
    </section>
  );
};

export default Hero;
